import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Users, Calendar, Workflow, Globe } from "lucide-react";
import Button from "../../../../components/ui/Button/Button";
import { servicesData } from "../../../../data/services";
import "./Services.css";

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Users: <Users size={32} strokeWidth={1.5} />,
  Calendar: <Calendar size={32} strokeWidth={1.5} />,
  Workflow: <Workflow size={32} strokeWidth={1.5} />,
  Globe: <Globe size={32} strokeWidth={1.5} />,
};

const Services = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const gridRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Left content stagger
      const leftElements = leftRef.current.querySelectorAll(".animate-in");
      gsap.fromTo(
        leftElements,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      // Cards stagger
      const cards = gridRef.current.querySelectorAll(".service-card");
      gsap.fromTo(
        cards,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleServiceClick = (e, link) => {
    e.preventDefault();
    const hash = link.split("#")[1];
    navigate("/services");
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 90,
          behavior: "smooth",
        });
      }
    }, 400);
  };

  return (
    <section className="services" id="services" ref={sectionRef}>
      <div className="services__container">
        <div className="services__left" ref={leftRef}>
          <div className="services__badge animate-in">{servicesData.badge}</div>
          <h2 className="services__headline animate-in">
            {servicesData.headline}
          </h2>
          <p className="services__description animate-in">
            {servicesData.description}
          </p>

          <div className="services__callout animate-in">
            <p className="services__callout-text">{servicesData.callout}</p>
            <Button
              text={servicesData.ctaButton.text}
              variant="primary"
              link={servicesData.ctaButton.link}
            />
          </div>
        </div>

        <div className="services__right">
          <div className="services__grid" ref={gridRef}>
            {servicesData.services.map((service, index) => (
              <a
                href={service.link}
                key={index}
                className="service-card"
                onClick={(e) => handleServiceClick(e, service.link)}
              >
                <div className="service-card__icon">
                  {iconMap[service.iconName]}
                </div>
                <h3 className="service-card__title">{service.title}</h3>
                <p className="service-card__description">
                  {service.description}
                </p>
                <span className="service-card__arrow">Learn More →</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
